import type {
  CategoryBannerProps,
  BannerSidebarProps,
  SanityRefDeconstructed,
} from '~/types'

interface FormatListingProps {
  addBanner?: boolean
  addSidebarBanner?: boolean
  addSubCategories?: boolean
  banner?: CategoryBannerProps
  bannerSidebar?: BannerSidebarProps
  subCategories?: {
    categories: {
      category: SanityRefDeconstructed
    }[]
  }
}

export const formatListingFields = ({
  addBanner,
  addSidebarBanner,
  addSubCategories,
  banner,
  bannerSidebar,
  subCategories,
}: FormatListingProps) => {
  return {
    banners: {
      header: addBanner && banner ? banner : null,
      sidebar: addSidebarBanner && bannerSidebar ? bannerSidebar : null,
    },
    subCategories:
      addSubCategories &&
      subCategories?.categories &&
      subCategories.categories.length > 0
        ? subCategories?.categories
        : null,
  }
}

export const formatLunarImage = (url?: string, size?: string) => {
  if (!url) return ''

  // Determine which size image to use
  const sizeToUse = size ? '-' + size : '-medium'

  // Insert new item into whole path
  const paths = url.split('/')
  paths.splice(-1, 0, 'conversions')

  // Get the index of the last item
  const lastIndex = paths.length - 1

  // Edit the last item to contain a size
  const lastItem = paths[lastIndex].split('.')
  lastItem[0] = lastItem[0] + sizeToUse

  // Replace the last item with our new value
  paths.splice(lastIndex, 1, lastItem.join('.'))

  // Rejoin all our paths
  return paths.join('/')
}

/*
  Flattens Sanity portable text to plain text, for schema and meta values that
  cannot carry markup.
*/
export const portableTextToPlain = (blocks?: unknown): string => {
  if (!Array.isArray(blocks)) return ''

  return blocks
    .map(block => {
      const children = (block as { children?: { text?: string }[] })?.children

      return Array.isArray(children)
        ? children.map(c => c?.text ?? '').join('')
        : ''
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/*
  Splits a single line UK address into schema.org PostalAddress parts. Returns
  null when the shape is not recognised, so callers can fall back to plain text.
*/
export const parsePostalAddress = (address?: string | null) => {
  const parts = (address ?? '')
    .split(',')
    .map(e => e.trim())
    .filter(Boolean)

  if (parts.length < 3) return null

  const postalCode = parts[parts.length - 1]

  // Bail out if the last segment isn't a UK postcode
  if (!/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(postalCode)) return null

  return {
    streetAddress: parts.slice(0, -2).join(', '),
    addressLocality: parts[parts.length - 2],
    postalCode,
    addressCountry: 'GB',
  }
}
